/**
 * Classes Page - Manage school classes and sections
 */
import React, { useState, useEffect } from 'react';
import { Modal } from '../components/shared';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }
};

function AddClassForm({ onSubmit }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [formData, setFormData] = useState({ name: '', grade: '', academic_year: '2025-26' });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
      <select value={formData.name} onChange={(e) => { const g = e.target.selectedIndex; setFormData({...formData, name: e.target.value, grade: g}); }} className="w-full px-4 py-2 border rounded-lg" required>
        <option value="">{L('Select Class', 'कक्षा चुनें')}</option>
        <option value="Nursery">Nursery</option>
        <option value="LKG">LKG</option>
        <option value="UKG">UKG</option>
        {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => <option key={n} value={`Class ${n}`}>{L('Class', 'कक्षा')} {n}</option>)}
      </select>
      <input placeholder={L('Academic Year', 'शैक्षणिक वर्ष')} value={formData.academic_year} onChange={(e) => setFormData({...formData, academic_year: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
      <button type="submit" className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">{L('Create Class', 'कक्षा बनाएं')}</button>
    </form>
  );
}

function AddSectionForm({ classId, onSubmit }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [formData, setFormData] = useState({ name: 'A', capacity: 40 });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...formData, class_id: classId }); }} className="space-y-4">
      <select value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
        {['A','B','C','D','E'].map(s => <option key={s} value={s}>{L('Section', 'सेक्शन')} {s}</option>)}
      </select>
      <input type="number" placeholder={L('Capacity', 'क्षमता')} value={formData.capacity} onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})} className="w-full px-4 py-2 border rounded-lg" />
      <button type="submit" className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">{L('Create Section', 'सेक्शन बनाएं')}</button>
    </form>
  );
}

export default function ClassesPage({ token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddClass, setShowAddClass] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);

  useEffect(() => { loadClasses(); }, []);

  const loadClasses = async () => {
    const res = await api.get('/api/classes/', token);
    setClasses(res.classes || []);
    setLoading(false);
  };

  const handleAddClass = async (data) => {
    await api.post('/api/classes/', data, token);
    loadClasses();
  };

  const handleAddSection = async (data) => {
    await api.post('/api/classes/sections', data, token);
    loadClasses();
  };

  if (loading) return <div className="p-8 text-center">{L('Loading...', 'लोड हो रहा है...')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">{L('Classes & Sections', 'कक्षाएं व सेक्शन')}</h2>
        <button onClick={() => setShowAddClass(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">+ {L('Add Class', 'कक्षा जोड़ें')}</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {classes.map(cls => (
          <div key={cls.id} className="bg-white rounded-xl shadow p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">{cls.name}</h3>
              <span className="text-sm text-gray-500">{cls.academic_year}</span>
            </div>
            <div className="space-y-2">
              {cls.sections?.map(section => (
                <div key={section.id} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                  <span>{L('Section', 'सेक्शन')} {section.name}</span>
                  <span className="text-sm text-gray-500">{section.student_count} {L('students', 'विद्यार्थी')}</span>
                </div>
              ))}
              {(!cls.sections || cls.sections.length === 0) && (
                <p className="text-gray-400 text-sm">{L('No sections yet', 'अभी कोई सेक्शन नहीं')}</p>
              )}
            </div>
            <button onClick={() => { setSelectedClass(cls); setShowAddSection(true); }}
              className="mt-3 w-full py-2 text-orange-600 border border-orange-600 rounded-lg hover:bg-orange-50">
              + {L('Add Section', 'सेक्शन जोड़ें')}
            </button>
          </div>
        ))}
      </div>

      {classes.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p className="text-4xl mb-4">📚</p>
          <p>{L('No classes created yet', 'अभी तक कोई कक्षा नहीं बनाई')}</p>
        </div>
      )}

      <Modal isOpen={showAddClass} onClose={() => setShowAddClass(false)} title={L('Add New Class', 'नई कक्षा जोड़ें')}>
        <AddClassForm onSubmit={(data) => { handleAddClass(data); setShowAddClass(false); }} />
      </Modal>

      <Modal isOpen={showAddSection} onClose={() => setShowAddSection(false)} title={`${L('Add Section to', 'सेक्शन जोड़ें')} ${selectedClass?.name}`}>
        <AddSectionForm classId={selectedClass?.id} onSubmit={(data) => { handleAddSection(data); setShowAddSection(false); }} />
      </Modal>
    </div>
  );
}
